import * as React from 'react';

import { useEffect, useState } from 'react';
import { LAKE_FORMATION_DATASOURCE_ID, TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { createPublishedPermissionDetailLink, createWorkspaceDetailLink, Page } from 'src/routes';
import { getFgaPolicy } from 'src/api/permissions';
import { Redirect } from 'react-router-dom';
import { Container, Flashbar, Header, KeyValuePairs, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';

import { PageHeader } from 'src/components/common/PageHeader';
import { DataBadges } from 'src/components/catalog/common';
import {
  fetchAllGlobalGovernanceTagsInHierarchy,
  getGlossariesForResource,
} from 'src/components/workspaces/common/common';
import { generateArnFromIdAndScope, IdInfo, Scope } from 'src/components/utils/arnUtil';

export interface ViewFgaPolicyProps {
  setContentType: any;
  location: any;
  match: any;
  activeGroup: string;
  activeWorkspace: any;
}

const ViewFgaPolicy = (props: ViewFgaPolicyProps) => {
  const [redirect, setRedirect] = useState(undefined);
  const [fgaPolicyId, setFgaPolicyId] = useState(undefined);
  const [fgaPolicy, setFgaPolicy] = useState(undefined);
  const [notifications, setNotifications] = useState([]);
  const [governanceTags, setGovernanceTags] = useState(undefined);

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    const activeWorkspace = props.activeWorkspace;
    // only allow workspace users
    if (activeWorkspace) {
      setFgaPolicyId(props.match.params.id);
      console.log('policy id retrived:', props.match.params.id);
    } else {
      setRedirect(Page.HOME);
    }
  }, []);

  useEffect(() => {
    if (fgaPolicyId) {
      getFgaPolicyForGivenId();
    }
  }, [fgaPolicyId]);

  const handleRefresh = async () => {
    await getFgaPolicyForGivenId();
  };

  const getFgaPolicyForGivenId = async () => {
    try {
      let getFgaPolicyResult = await getFgaPolicy({
        id: fgaPolicyId,
      });
      let fineGrainAccess = getFgaPolicyResult.FineGrainAccess;
      setFgaPolicy(fineGrainAccess);
      // Fetch all Global governance tags in hierarchy.
      if ('FineGrainedAccess-LF' === fineGrainAccess?.type) {
        const idInfo: IdInfo = {
          dataSource: LAKE_FORMATION_DATASOURCE_ID,
          catalogId: fineGrainAccess.tableData?.tableCatalogId,
          databaseName: fineGrainAccess.tableData?.databaseName,
          tableName: fineGrainAccess.tableData?.tableName,
          region: fineGrainAccess.tableData?.region,
          fineGrainAccessPolicyId: fineGrainAccess.id,
        };
        const allGlossaries = await getGlossariesForResource(
          generateArnFromIdAndScope(Scope.FineGrainedAccessPolicy, idInfo),
        );
        const fgaGlobalGovernanceTags = await fetchAllGlobalGovernanceTagsInHierarchy(allGlossaries);
        setGovernanceTags(fgaGlobalGovernanceTags);
      }
    } catch (err) {
      setNotification('error', err.message);
    }
  };
  const buttonOptions = () => {
    return [
      {
        text: '',
        icon: 'refresh',
        onItemClick: handleRefresh,
      },
    ];
  };

  const setNotification = async (header, message) => {
    console.log('set notification');
    if (header === 'success') {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  return (
    <div>
      {redirect && <Redirect push to={redirect} />}

      <div id='flash-bar'>
        <Flashbar items={notifications} />
      </div>
      <SpaceBetween direction={'vertical'} size={'xxs'}>
        <PageHeader buttons={buttonOptions()} header={`Policy ID: ${fgaPolicyId}`} />
        <DataBadges governanceTags={governanceTags} />
        <Container header={<Header variant={'h2'}>Policy details</Header>}>
          <KeyValuePairs
            columns={4}
            items={[
              {
                label: 'Owner',
                value: <Link href={createWorkspaceDetailLink(fgaPolicy?.ownerId)}>{fgaPolicy?.ownerId}</Link>,
              },
              { label: 'Policy name', value: fgaPolicy?.tableData?.name },
              { label: 'Description', value: fgaPolicy?.description },
              { label: 'Scope', value: fgaPolicy?.scope },
              { label: 'Type', value: fgaPolicy?.type },
              { label: 'Status', value: fgaPolicy?.status },
              { label: 'Status reason', value: fgaPolicy?.statusReason },
              {
                label: 'Data permission',
                value: (
                  <Link href={createPublishedPermissionDetailLink(fgaPolicy?.dataPermissionId)}>
                    {fgaPolicy?.dataPermissionId}
                  </Link>
                ),
              },
              { label: 'Created on', value: fgaPolicy?.createDate },
              { label: 'Updated on', value: fgaPolicy?.updateDate },
              fgaPolicy?.tableDate?.clusterId
                ? { label: 'Cluster identifier', value: fgaPolicy?.tableData?.clusterId }
                : undefined,
              { label: 'Database name', value: fgaPolicy?.tableData?.databaseName },
              fgaPolicy?.tableDate?.clusterId
                ? { label: 'Schema name', value: fgaPolicy?.tableData?.schemaName }
                : undefined,
              { label: 'Table name', value: fgaPolicy?.tableData?.tableName },
              {
                label: 'Column-level access',
                value: fgaPolicy?.tableData?.columnNames
                  ? 'Include'
                  : fgaPolicy?.tableData?.columnWildcard?.excludedColumnNames
                  ? 'Exclude'
                  : '*',
              },
              {
                label: 'Row filter expression',
                value: fgaPolicy?.tableData?.rowFilter?.filterExpression
                  ? fgaPolicy.tableData.rowFilter.filterExpression
                  : '*',
              },
              {
                label: 'Columns',
                value: fgaPolicy?.tableData?.columnNames
                  ? fgaPolicy?.tableData?.columnNames.join(',')
                  : fgaPolicy?.tableData?.columnWildcard?.excludedColumnNames
                  ? fgaPolicy?.tableData?.columnWildcard?.excludedColumnNames.join(',')
                  : null,
              },
            ].filter((item) => item != undefined)}
          />
        </Container>
      </SpaceBetween>
    </div>
  );
};

export default ViewFgaPolicy;
